// Fee & Obligation PR-5 — the Add Obligation wizard's Step 6 projected-impact
// preview. Replaces the amortization-only stub with the REAL projector: shows
// how this new obligation folds into the loan's next projected statement (the
// standing worksheet's forward projection). Silent when the projection is
// unavailable — the review still stands without it.

import { formatCurrency, formatCalendarDate } from "@/design-system/lib/format";
import { useForwardProjection } from "@/hooks/portfolioAdmin/useForwardProjection";

function money(value: number): string {
  return formatCurrency(value, { maximumFractionDigits: 2 });
}

export function ProjectedImpactPanel({
  loanId,
  amount,
  typeLabel,
  priorityLabel,
  lifecycleLabel,
}: {
  loanId: string;
  amount: number;
  typeLabel: string;
  /** e.g. "P3 — Late Fee", or null when the type has no live WF1 slot. */
  priorityLabel: string | null;
  lifecycleLabel: string;
}) {
  const { data, isLoading } = useForwardProjection(loanId);

  if (isLoading || !data || data.nextProjectedIndex == null) return null;
  const next = data.periods.find((p) => p.periodIndex === data.nextProjectedIndex);
  if (!next) return null;

  const projectedDue = next.amountDue + amount;
  const month = formatCalendarDate(next.dueDate, { month: "long", year: "numeric", day: undefined });

  return (
    <div
      className="rounded-lg border border-border-light bg-white-warm p-space-3"
      data-testid="projected-impact"
    >
      <span className="text-small font-semibold text-onyx">Projected impact</span>
      <p className="text-small text-gray-mid m-0 mt-space-1">
        On the next projected statement ({month}), this {money(amount)} {typeLabel}
        {priorityLabel ? ` appears at ${priorityLabel}` : ""}, collected toward $0
        {lifecycleLabel ? ` (${lifecycleLabel.toLowerCase()})` : ""}.
      </p>
      <div className="mt-space-2 flex items-center justify-between gap-space-3 text-small">
        <span className="text-gray-mid">Projected amount due</span>
        <span className="tabular-nums text-onyx">
          {money(next.amountDue)} →{" "}
          <span className="font-semibold">{money(projectedDue)}</span>
        </span>
      </div>
    </div>
  );
}
